import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogForm, LtrToken, BidiText, EmptyState } from '../../../shared/components/common';
import { LocalizedCurrencyDisplay, LocalizedDateDisplay } from '../../../shared/components';
import { LocalizedMonetaryInput } from '../../../shared/components/LocalizedMonetaryInput';
import { CompanyFormField } from '../../../shared/components/multi-company';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { useActiveCompany } from '../../../shared/contexts/ActiveCompanyContext';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useAccounts } from '../hooks/useAccounts';
import { useInvoices } from '../hooks/useInvoices';
import { useBills } from '../hooks/useBills';
import { bankDepositApi } from '../services/friendlyApi';
import { filterCashAndBankAccounts } from '../utils/cashAndBankAccounts';
import {
  selectAllocatableInvoices,
  selectAllocatableBills,
  autoAllocateAmounts,
  autoAllocateBillAmounts,
  sumAllocations,
} from '../utils/paymentAllocation';
import type { PaymentCreate, PaymentType } from '../types';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

const PAYMENT_METHOD_KINDS = [
  'cash',
  'check',
  'ach',
  'wire',
  'card',
  'digital_wallet',
  'other',
] as const;

type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];

/** A document (invoice or bill) a payment can be allocated to — the shared shape
 * the allocation grid renders, projected from either an {@link Invoice} (AR) or a
 * {@link Bill} (AP) so one grid serves both payment directions. */
interface AllocatableDoc {
  id: string;
  number: string;
  amount_due: number;
  currency_code: string;
  due_date?: string | null;
}

interface PaymentQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentCreate) => void;
  isLoading?: boolean;
  defaultPaymentType?: PaymentType;
  /** Prefill the customer/vendor (e.g. opened from an invoice/bill's Record payment). */
  defaultContactId?: string;
  defaultContactName?: string;
  /** Prefill an allocation to this invoice at its open balance (received only). */
  defaultInvoiceId?: string;
  /** Prefill an allocation to this bill at its open balance (made only). */
  defaultBillId?: string;
  /** Lock the contact picker when the payer is fixed by the source document. */
  lockContact?: boolean;
}

export function PaymentQuickCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading,
  defaultPaymentType,
  defaultContactId,
  defaultContactName,
  defaultInvoiceId,
  defaultBillId,
  lockContact,
}: PaymentQuickCreateDialogProps) {
  const { t } = useTranslation('accounting');
  const { data: accountsData } = useAccounts({ is_active: true, limit: 500 });

  const { activeCompanyId } = useActiveCompany();
  const { data: myCompaniesData } = useMyCompanies();
  const hasMultipleCompanies = (myCompaniesData?.data?.length ?? 0) > 1;
  const [companyId, setCompanyId] = useState('');
  const [paymentType, setPaymentType] = useState<PaymentType>(defaultPaymentType || 'received');
  const [contactId, setContactId] = useState('');
  const [contactName, setContactName] = useState('');
  const [amount, setAmount] = useState('');
  const [depositToAccountId, setDepositToAccountId] = useState('');
  const [paymentDate, setPaymentDate] = useState(toDateOnlyString(new Date()));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodKind | ''>('');
  const [reference, setReference] = useState('');
  const [bankTransactionNumber, setBankTransactionNumber] = useState('');
  const [notes, setNotes] = useState('');
  // Per-document allocation amounts (keyed by invoice/bill id).
  const [allocations, setAllocations] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (open) {
      setCompanyId(activeCompanyId ?? '');
      setPaymentType(defaultPaymentType || 'received');
      setContactId(defaultContactId ?? '');
      setContactName(defaultContactName ?? '');
      setAmount('');
      setDepositToAccountId('');
      setPaymentDate(toDateOnlyString(new Date()));
      setPaymentMethod('');
      setReference('');
      setBankTransactionNumber('');
      setNotes('');
      setAllocations({});
    }
  }, [open, activeCompanyId, defaultPaymentType, defaultContactId, defaultContactName]);

  const isMade = paymentType === 'made';

  // T19 — when payment method kind changes on a received payment, ask the
  // backend for the default deposit-to account (clearing for cash/check,
  // configured bank for ach/wire/card/digital_wallet) and pre-select it
  // unless the user has already picked one.
  useEffect(() => {
    if (!open) return;
    if (paymentType !== 'received') return;
    if (!paymentMethod) return;
    if (depositToAccountId) return; // honor explicit user choice
    let cancelled = false;
    bankDepositApi
      .defaultRouting(paymentMethod)
      .then((res) => {
        if (cancelled) return;
        const suggested = res.data?.deposit_to_account_id;
        if (suggested) setDepositToAccountId(suggested);
      })
      .catch(() => {
        /* non-fatal; user can still pick manually */
      });
    return () => {
      cancelled = true;
    };
  }, [open, paymentType, paymentMethod, depositToAccountId]);

  const accounts = accountsData?.data;
  const bankAccounts = useMemo(
    () => filterCashAndBankAccounts(accounts || []),
    [accounts],
  );

  // Open receivables (AR) or payables (AP) for the selected contact. Gated on
  // `open` + the matching direction so a mounted-but-closed dialog fetches
  // nothing, and a received payment never fetches bills (and vice-versa).
  const showAllocations = !!contactId;
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices(
    !isMade && contactId ? { customer_id: contactId, limit: 200 } : undefined,
    { enabled: open && showAllocations && !isMade },
  );
  // Fetch the vendor's bills and let `selectAllocatableBills` pick the open ones
  // client-side. NB: don't pass `status: 'due'` — the list endpoint narrows that
  // to `due_date >= today`, which would drop OVERDUE (still-open) bills; the
  // stored lifecycle status of an overdue bill is 'due', so the client filter
  // (status==='due' && amount_due>0) catches them while paid bills (amount_due=0)
  // fall out.
  const { data: billsData, isLoading: billsLoading } = useBills(
    isMade && contactId ? { vendor_id: contactId, limit: 200 } : undefined,
    { enabled: open && showAllocations && isMade },
  );

  // A payment settles one currency; anchor to the source document (document-driven
  // flow) or the first open document and show only same-currency open items.
  const { openInvoices, allocationCurrency: invoiceCurrency } = useMemo(
    () => selectAllocatableInvoices(invoicesData?.data ?? [], defaultInvoiceId),
    [invoicesData, defaultInvoiceId],
  );
  const { openBills, allocationCurrency: billCurrency } = useMemo(
    () => selectAllocatableBills(billsData?.data ?? [], defaultBillId),
    [billsData, defaultBillId],
  );
  const allocationCurrency = isMade ? billCurrency : invoiceCurrency;
  const allocationsLoading = isMade ? billsLoading : invoicesLoading;

  // Unified rows for the allocation grid — one shape, both directions.
  const openDocuments = useMemo<AllocatableDoc[]>(
    () =>
      isMade
        ? openBills.map((bill) => ({
            id: bill.id,
            number: bill.bill_number,
            amount_due: bill.amount_due,
            currency_code: bill.currency_code,
            due_date: bill.due_date ?? null,
          }))
        : openInvoices.map((inv) => ({
            id: inv.id,
            number: inv.invoice_number,
            amount_due: inv.amount_due,
            currency_code: inv.currency_code,
            due_date: inv.due_date ?? null,
          })),
    [isMade, openBills, openInvoices],
  );

  // Seed the source document's full open balance once its data has loaded.
  const sourceDocId = isMade ? defaultBillId : defaultInvoiceId;
  useEffect(() => {
    if (!open || !sourceDocId) return;
    const source = openDocuments.find((doc) => doc.id === sourceDocId);
    if (!source) return;
    setAllocations((prev) =>
      prev[sourceDocId] === undefined
        ? { ...prev, [sourceDocId]: Number(source.amount_due) }
        : prev,
    );
  }, [open, sourceDocId, openDocuments]);

  const allocationTotal = useMemo(() => sumAllocations(allocations), [allocations]);
  const isAllocating = showAllocations && allocationTotal > 0;
  const effectiveAmount = isAllocating ? allocationTotal : parseFloat(amount);
  const anyLineOverDue = openDocuments.some(
    (doc) => Number(allocations[doc.id] || 0) > Number(doc.amount_due) + 0.0001,
  );

  const setLineAmount = (docId: string, value: number | null) =>
    setAllocations((prev) => ({ ...prev, [docId]: value }));

  /** Greedily fill each open document's balance up to the entered amount, or fill
   * every balance when no amount is set yet. */
  const autoAllocate = () =>
    setAllocations(
      isMade
        ? autoAllocateBillAmounts(openBills, parseFloat(amount))
        : autoAllocateAmounts(openInvoices, parseFloat(amount)),
    );

  const canSubmit =
    !!depositToAccountId &&
    effectiveAmount > 0 &&
    !anyLineOverDue &&
    !(!companyId && activeCompanyId === null && hasMultipleCompanies);

  const handleSubmit = () => {
    if (!canSubmit) return;

    const lines = isAllocating
      ? openDocuments
          .filter((doc) => Number(allocations[doc.id] || 0) > 0)
          .map((doc) => ({
            allocated_to_type: isMade ? 'bill' : 'invoice',
            allocated_to_id: doc.id,
            amount: Number(allocations[doc.id]),
          }))
      : undefined;

    onSubmit({
      company_id: companyId || undefined,
      payment_type: paymentType,
      amount: effectiveAmount,
      deposit_to_account_id: depositToAccountId,
      payment_date: paymentDate,
      contact_id: contactId || undefined,
      contact_name: contactName || undefined,
      currency_code: isAllocating ? allocationCurrency : undefined,
      payment_method: paymentMethod || undefined,
      payment_method_kind: paymentMethod || undefined,
      reference: reference || undefined,
      bank_transaction_number: bankTransactionNumber || undefined,
      notes: notes || undefined,
      allocations: lines,
    });
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('payments.createPayment')}
      submitLabel={t('payments.createPayment')}
      isLoading={isLoading}
      isDisabled={!canSubmit}
      maxWidth={showAllocations ? 'lg' : 'md'}
    >
      <div className="space-y-4">
        <CompanyFormField value={companyId} onChange={setCompanyId} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('payments.type')}</Label>
            <Select value={paymentType} onValueChange={(v) => setPaymentType(v as PaymentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="received">{t('paymentTypes.received')}</SelectItem>
                <SelectItem value="made">{t('paymentTypes.made')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('payments.date')}</Label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>
            {paymentType === 'received' ? t('payments.customer') : t('payments.vendor')}
          </Label>
          {lockContact ? (
            <Input value={contactName} disabled />
          ) : (
            <OrganizationPicker
              value={contactId}
              onChange={(id) => setContactId(id)}
              onCompanySelected={(company) => setContactName(company?.name ?? '')}
              organizationRelationshipFilter={paymentType === 'received' ? 'customer' : 'vendor'}
              placeholder={t('payments.contactPlaceholder')}
              allowClear
            />
          )}
        </div>

        {/* Allocation grid — apply the payment to the contact's open invoices (AR)
            or open bills (AP). */}
        {showAllocations && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {isMade ? t('payments.applyToBills') : t('payments.applyToInvoices')}
              </span>
              {openDocuments.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={autoAllocate}
                >
                  {t('payments.autoAllocate')}
                </button>
              )}
            </div>
            {allocationsLoading ? (
              <p className="text-sm text-muted-foreground">{t('common:app.loading', 'Loading…')}</p>
            ) : openDocuments.length === 0 ? (
              <EmptyState
                title={isMade ? t('payments.noOpenBillsTitle') : t('payments.noOpenInvoicesTitle')}
                message={isMade ? t('payments.noOpenBillsMessage') : t('payments.noOpenInvoicesMessage')}
              />
            ) : (
              <div className="space-y-2">
                {openDocuments.map((doc) => {
                  const lineOver =
                    Number(allocations[doc.id] || 0) > Number(doc.amount_due) + 0.0001;
                  return (
                    <div
                      key={doc.id}
                      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          <LtrToken>{doc.number}</LtrToken>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('payments.due')}:{' '}
                          <LocalizedCurrencyDisplay
                            value={doc.amount_due}
                            currency={doc.currency_code}
                          />
                          {doc.due_date && (
                            <>
                              {' · '}
                              <LocalizedDateDisplay dateOnly date={doc.due_date} />
                            </>
                          )}
                        </p>
                      </div>
                      <div className="w-full sm:w-44">
                        <LocalizedMonetaryInput
                          value={allocations[doc.id] ?? null}
                          onChange={(v) => setLineAmount(doc.id, v)}
                          currency={doc.currency_code}
                          min={0}
                          max={Number(doc.amount_due)}
                          placeholder="0.00"
                        />
                        {lineOver && (
                          <p className="mt-0.5 text-xs text-destructive">
                            {t('payments.exceedsDue')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('payments.amount')}</Label>
            {isAllocating && allocationCurrency ? (
              <div
                className="flex h-10 items-center rounded-md border border-border bg-muted/40 px-3 text-sm font-semibold"
                data-testid="payment-amount"
              >
                <LocalizedCurrencyDisplay value={allocationTotal} currency={allocationCurrency} />
              </div>
            ) : (
              <Input
                type="number"
                min={0}
                step={0.01}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="payment-amount"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>{t('payments.bankAccount')}</Label>
            <Select value={depositToAccountId} onValueChange={setDepositToAccountId}>
              <SelectTrigger data-testid="payment-bank-account">
                <SelectValue placeholder={t('payments.selectBankAccount')} />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map((acc) => (
                  <SelectItem key={acc.id} value={acc.id}>
                    <LtrToken>{acc.account_number}</LtrToken> - <BidiText>{acc.name}</BidiText>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('payments.method')}</Label>
          <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as PaymentMethodKind)}>
            <SelectTrigger>
              <SelectValue placeholder={t('payments.methodPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHOD_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {t(`payments.methodKinds.${kind}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {paymentType === 'received' && (
          <p className="text-xs text-muted-foreground">
            {t('payments.routingHint')}
          </p>
        )}

        <div className="space-y-2">
          <Label>{t('payments.reference')}</Label>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('payments.referencePlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('payments.bankTransactionNumber')}</Label>
          <Input
            value={bankTransactionNumber}
            onChange={(e) => setBankTransactionNumber(e.target.value)}
            placeholder={t('payments.bankTransactionNumberPlaceholder')}
            maxLength={50}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('payments.notes')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </div>
      </div>
    </DialogForm>
  );
}
